import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GitCompare, Check, Search, Edit, Loader2 } from 'lucide-react';
import { ReactPhotoEditor } from 'react-photo-editor';
import { collageLayouts } from '../components/Collegelayout';
import LayoutSelector from '../components/Layoutselector';

/* ---------------- HELPER TO PARSE GRID LAYOUTS ---------------- */

const parseGridTemplate = (template, totalSize) => {
  const parts = template.split(" ").map(p => parseFloat(p));
  const totalFr = parts.reduce((a, b) => a + b, 0);
  let currentPos = 0;
  return parts.map(fr => {
    const size = (fr / totalFr) * totalSize;
    const start = currentPos;
    currentPos += size;
    return { start, size };
  });
};

const getLayoutRects = (layout, width, height) => {
  if (!layout) return [];

  const cols = parseGridTemplate(layout.gridTemplateColumns, width);
  const rows = parseGridTemplate(layout.gridTemplateRows, height);

  const cellMap = {};

  layout.areas.forEach((rowStr, rowIndex) => {
    const cells = rowStr.trim().split(/\s+/);
    cells.forEach((areaName, colIndex) => {
      if (!cellMap[areaName]) {
        cellMap[areaName] = { minC: colIndex, maxC: colIndex, minR: rowIndex, maxR: rowIndex };
      } else {
        cellMap[areaName].minC = Math.min(cellMap[areaName].minC, colIndex);
        cellMap[areaName].maxC = Math.max(cellMap[areaName].maxC, colIndex);
        cellMap[areaName].minR = Math.min(cellMap[areaName].minR, rowIndex);
        cellMap[areaName].maxR = Math.max(cellMap[areaName].maxR, rowIndex);
      }
    });
  });

  return ["a", "b", "c", "d"]
    .filter(area => cellMap[area])
    .map(area => {
      const { minC, maxC, minR, maxR } = cellMap[area];
      const x = cols[minC].start;
      const y = rows[minR].start;
      const right = cols[maxC].start + cols[maxC].size;
      const bottom = rows[maxR].start + rows[maxR].size;
      return { x, y, w: right - x, h: bottom - y };
    });
};

import {
  usePatientList,
  usePatientProfile,
  useBeforeAfter
} from '../hooks/useApi';

import { useDebounce } from '../hooks/useDebounce';

const BeforeAfter = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [searchParams] = useSearchParams();
  const urlPatientId = searchParams.get('patientId');

  const [selectedPatientId, setSelectedPatientId] = useState(urlPatientId || '');
  const [search, setSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  const patientSelectRef = useRef(null);

  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [collageTitle, setCollageTitle] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState(collageLayouts.two[0]); // Default horizontal

  // Photo Editor State
  const [showEditor, setShowEditor] = useState(false);
  const [editingPhotoIndex, setEditingPhotoIndex] = useState(null); // 0 for before, 1 for after
  const [currentEditingImage, setCurrentEditingImage] = useState(null);
  const [isLoadingEditor, setIsLoadingEditor] = useState(false);

  /* -------------------- API HOOKS -------------------- */

  const debouncedSearch = useDebounce(search, 500);

  const { data: patientListData, isLoading: isLoadingPatientList } =
    usePatientList(debouncedSearch, {
      enabled: isSearching && debouncedSearch.length > 0,
    });

  const patientList = patientListData?.data || [];

  const { data: patientData, isLoading: isLoadingPatientPhotos } =
    usePatientProfile(selectedPatientId);

  const patient = patientData?.data || {};
  const photos = patient?.photos || [];

  const { mutate: createBeforeAfter, isPending: isCreatingBeforeAfter } =
    useBeforeAfter();

  /* -------------------- EFFECTS -------------------- */

  useEffect(() => {
    if (urlPatientId && patient?.full_name && !search) {
      setSearch(patient.full_name);
      setIsSearching(false);
      setShowPatientDropdown(false);
    }
  }, [urlPatientId, patient?.full_name, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        patientSelectRef.current &&
        !patientSelectRef.current.contains(event.target)
      ) {
        setShowPatientDropdown(false);
      }
    };

    if (showPatientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPatientDropdown]);

  useEffect(() => {
    if (!selectedPatientId) {
      setSelectedPhotos([]);
      setCollageTitle('');
    }
  }, [selectedPatientId]);

  /* -------------------- HELPERS -------------------- */

  const cleanUrl = (url = '') => url.replace(/\\/g, '');

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  /* -------------------- HANDLERS -------------------- */

  const handleSelectPatient = (patient) => {
    setSelectedPatientId(String(patient.id));
    setSearch(patient.full_name || '');
    setShowPatientDropdown(false);
    setIsSearching(false);
    setSelectedPhotos([]);
    setCollageTitle('');
  };

  const handleSelectPhoto = (photo) => {
    const isSelected = selectedPhotos.find((p) => p.id === photo.id);

    if (isSelected) {
      setSelectedPhotos(selectedPhotos.filter((p) => p.id !== photo.id));
      return;
    }

    if (selectedPhotos.length >= 2) {
      toast.error('You can select only 2 photos');
      return;
    }

    const newSelection = [...selectedPhotos, photo].sort(
      (a, b) =>
        new Date(a.captured_at || a.capturedAt) -
        new Date(b.captured_at || b.capturedAt)
    );

    setSelectedPhotos(newSelection);

    if (newSelection.length === 2) {
      const diffDays = Math.floor(
        (new Date(newSelection[1].captured_at || newSelection[1].capturedAt) -
          new Date(newSelection[0].captured_at || newSelection[0].capturedAt)) /
        (1000 * 60 * 60 * 24)
      );

      setCollageTitle(
        `${patient?.diagnosis || 'Treatment'} Progress - ${diffDays} days`
      );
    }
  };

  const generateBeforeAfterImage = async (beforeUrl, afterUrl) => {
    const loadImage = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.src = src;
      });

    const beforeImg = await loadImage(beforeUrl);
    const afterImg = await loadImage(afterUrl);

    // Determine Canvas Dimensions based on Layout
    const maxImgW = Math.max(beforeImg.width, afterImg.width);
    const maxImgH = Math.max(beforeImg.height, afterImg.height);

    let canvasW, canvasH;

    // Simple heuristic: if vertical layout, double height. If horizontal, double width.
    if (selectedLayout.id.includes('vertical')) {
      canvasW = maxImgW;
      canvasH = maxImgH * 2;
    } else {
      canvasW = maxImgW * 2;
      canvasH = maxImgH;
    }

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;

    const ctx = canvas.getContext('2d');

    // Fill white
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvasW, canvasH);

    /* ---------- DRAW IMAGES ---------- */
    const rects = getLayoutRects(selectedLayout, canvasW, canvasH);
    const images = [beforeImg, afterImg];

    images.forEach((img, index) => {
      if (index < rects.length) {
        const { x, y, w, h } = rects[index];
        // Cover fit logic
        const imgRatio = img.width / img.height;
        const targetRatio = w / h;
        let renderW, renderH, renderX, renderY;

        if (imgRatio > targetRatio) {
          renderH = h;
          renderW = h * imgRatio;
          renderX = x - (renderW - w) / 2;
          renderY = y;
        } else {
          renderW = w;
          renderH = w / imgRatio;
          renderX = x;
          renderY = y - (renderH - h) / 2;
        }

        ctx.save();
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.clip();
        ctx.drawImage(img, renderX, renderY, renderW, renderH);
        ctx.restore();

        // Draw border
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 10;
        ctx.strokeRect(x, y, w, h);
      }
    });

    /* ---------- TEXT STYLE ---------- */
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Background shadow for readability
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 6;

    /* ---------- LABELS ---------- */
    // Place labels centered in their respective slots
    if (rects.length >= 2) {
      ctx.fillStyle = '#ffffff';
      // Before
      ctx.fillText('BEFORE', rects[0].x + rects[0].w / 2, rects[0].y + 20);
      // After
      ctx.fillText('AFTER', rects[1].x + rects[1].w / 2, rects[1].y + 20);
    }

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(
            new File([blob], 'before-after.jpg', { type: 'image/jpeg' })
          );
        },
        'image/jpeg',
        0.9
      );
    });
  };


  const handleCreateCollage = async () => {
    if (selectedPhotos.length !== 2) {
      toast.error('Select exactly 2 photos');
      return;
    }

    if (!collageTitle.trim()) {
      toast.error('Title is required');
      return;
    }

    try {
      setIsGenerating(true);

      const sorted = [...selectedPhotos].sort(
        (a, b) =>
          new Date(a.captured_at || a.capturedAt) -
          new Date(b.captured_at || b.capturedAt)
      );

      const collageFile = await generateBeforeAfterImage(
        cleanUrl(sorted[0].editedPreview || sorted[0].original_url),
        cleanUrl(sorted[1].editedPreview || sorted[1].original_url)
      );

      const formData = new FormData();
      formData.append('patient_id', selectedPatientId);
      formData.append('collage_title', collageTitle);
      formData.append('photo_ids[]', sorted[0].id);
      formData.append('photo_ids[]', sorted[1].id);
      formData.append('before_after_collage_image', collageFile);

      createBeforeAfter(formData, {
        onSuccess: () => {
          toast.success('Before/After collage created');
          navigate(`/patients/${selectedPatientId}`);
        },
        onError: () => toast.error('Failed to create collage'),
        onSettled: () => setIsGenerating(false),
      });
    } catch {
      setIsGenerating(false);
      toast.error('Failed to generate image');
    }
  };



  const handleEditPhoto = async (index) => {
    try {
      setIsLoadingEditor(true);
      const photo = selectedPhotos[index];
      const imageUrl = photo.editedPreview || cleanUrl(photo.original_url || photo.thumbnail_url);

      // Fetch the image and convert to Blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `photo-${index}.jpg`, { type: blob.type || 'image/jpeg' });

      setCurrentEditingImage(file);
      setEditingPhotoIndex(index);
      setShowEditor(true);
    } catch (error) {
      console.error('Error loading image:', error);
      toast.error('Failed to load image for editing');
    } finally {
      setIsLoadingEditor(false);
    }
  };




  const handleSaveEditedPhoto = (editedFile) => {
    // Create a preview URL from the edited file
    const previewUrl = URL.createObjectURL(editedFile);

    // Update the selected photo with the edited preview
    const updatedPhotos = [...selectedPhotos];
    updatedPhotos[editingPhotoIndex] = {
      ...updatedPhotos[editingPhotoIndex],
      editedPreview: previewUrl,
    };

    setSelectedPhotos(updatedPhotos);
    setShowEditor(false);
    setEditingPhotoIndex(null);
    setCurrentEditingImage(null);
    toast.success('Photo edited successfully');
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingPhotoIndex(null);
    setCurrentEditingImage(null);
  };

  /* -------------------- RENDER -------------------- */

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT PANEL OMITTED (UNCHANGED) */}
        <div className=" space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Select Patient & Photos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Patient <span className="text-xs text-red-500">*</span></Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="patient-search"
                    placeholder="Search for a patient"
                    value={search}
                    onFocus={() => setIsSearching(true)}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);

                      // Clear selected patient when input is empty
                      if (!value.trim()) {
                        setSelectedPatientId('');
                        setShowPatientDropdown(false);
                        setIsSearching(false);
                        return;
                      }

                      setShowPatientDropdown(true);
                      setIsSearching(true);
                    }}
                    className="pl-10"
                  />
                  {selectedPatientId && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                </div>


                {/* Patient Dropdown */}
                {showPatientDropdown && (
                  <div
                    ref={patientSelectRef}
                    className="z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                  >
                    {isLoadingPatientList ? (
                      <div className="p-4 text-center text-gray-500">Loading patients...</div>
                    ) : patientList.length > 0 ? (
                      patientList.map((patient) => (
                        <div
                          key={patient.id}
                          onClick={() => handleSelectPatient(patient)}
                          className={`p-3 cursor-pointer hover:bg-gray-100 transition-colors ${String(selectedPatientId) === String(patient.id) ? 'bg-primary-50' : ''
                            }`}
                        >
                          <div className="font-medium text-gray-900">{patient.full_name || 'N/A'}</div>
                          {patient.patient_uid && (
                            <div className="text-xs text-gray-500">ID: {patient.patient_uid}</div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">No patients found</div>
                    )}
                  </div>
                )}
              </div>

              {isLoadingPatientPhotos && selectedPatientId && (
                <div className="text-center py-4 text-gray-500">
                  <p>Loading patient photos...</p>
                </div>
              )}

              {selectedPatientId && !isLoadingPatientPhotos && photos.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No photos found for this patient.</p>
                  <Button
                    variant="link"
                    onClick={() => navigate(`/capture?patientId=${selectedPatientId}`)}
                    className="text-primary-600"
                  >
                    Capture photos first
                  </Button>
                </div>
              )}

              {photos.length > 0 && (
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Select 2 Photos <span className="text-xs text-red-500">*</span></Label>
                      <span className="text-sm text-gray-500">
                        {selectedPhotos.length} / 2 selected
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {photos.map((photo) => {
                        const isSelected = selectedPhotos.find(p => p.id === photo.id);
                        const selectionIndex = selectedPhotos.findIndex(p => p.id === photo.id);
                        return (
                          <div
                            key={photo.id}
                            className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${isSelected
                              ? 'border-primary-500 ring-2 ring-primary-200'
                              : 'border-gray-200 hover:border-primary-300'
                              }`}
                            onClick={() => handleSelectPhoto(photo)}
                          >
                            <img
                              src={cleanUrl(photo.thumbnail_url)}
                              alt="Photo"
                              className="w-full h-full object-cover"
                            />
                            {isSelected && (
                              <>
                                <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-white" />
                                </div>
                                <div className="absolute top-2 left-2 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                                  <span className="text-white text-xs font-bold">
                                    {selectionIndex === 0 ? '1' : '2'}
                                  </span>
                                </div>
                              </>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                              <p className="text-white text-xs">{formatDate(photo.captured_at || photo.capturedAt)}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {/* PREVIEW */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitCompare className="w-5 h-5" />
              Preview
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4">
            {selectedPhotos.length === 2 ? (
              <>
                <div className="space-y-4">
                  <div className="p-4 border rounded-xl bg-gray-50/50">
                    <Label className="mb-2 block">Select Layout</Label>
                    <LayoutSelector
                      layouts={collageLayouts.two}
                      onSelect={setSelectedLayout}
                      selectedLayoutId={selectedLayout?.id}
                    />
                  </div>

                  <div
                    style={{
                      display: "grid",
                      width: "100%",
                      aspectRatio: "1 / 1", // Or dynamic based on layout? Square is fine for preview area.
                      gap: 8,
                      gridTemplateColumns: selectedLayout?.gridTemplateColumns,
                      gridTemplateRows: selectedLayout?.gridTemplateRows,
                      gridTemplateAreas: selectedLayout?.areas?.map(r => `"${r}"`).join(" ")
                    }}
                  >
                    {/* BEFORE */}
                    <div
                      style={{
                        gridArea: "a",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        borderRadius: 8
                      }}
                    >
                      <p className="absolute top-2 left-2 z-10 text-xs text-white bg-black/50 px-2 rounded">Before</p>
                      <img
                        src={
                          selectedPhotos[0].editedPreview ||
                          cleanUrl(selectedPhotos[0].thumbnail_url)
                        }
                        alt="Before"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <button
                        onClick={() => handleEditPhoto(0)}
                        disabled={isLoadingEditor}
                        className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-lg disabled:opacity-50"
                      >
                        {isLoadingEditor && editingPhotoIndex === 0 ? (
                          <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                        ) : (
                          <Edit className="w-4 h-4 text-gray-700" />
                        )}
                      </button>
                    </div>

                    {/* AFTER */}
                    <div
                      style={{
                        gridArea: "b",
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                        borderRadius: 8
                      }}
                    >
                      <p className="absolute top-2 left-2 z-10 text-xs text-white bg-black/50 px-2 rounded">After</p>
                      <img
                        src={
                          selectedPhotos[1].editedPreview ||
                          cleanUrl(selectedPhotos[1].thumbnail_url)
                        }
                        alt="After"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <button
                        onClick={() => handleEditPhoto(1)}
                        disabled={isLoadingEditor}
                        className="absolute top-2 right-2 bg-white p-2 rounded-full shadow-lg disabled:opacity-50"
                      >
                        {isLoadingEditor && editingPhotoIndex === 1 ? (
                          <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                        ) : (
                          <Edit className="w-4 h-4 text-gray-700" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                <Label>Collage Title</Label>
                <Input
                  value={collageTitle}
                  onChange={(e) => setCollageTitle(e.target.value)}
                />

                <Button
                  onClick={handleCreateCollage}
                  disabled={isGenerating || isCreatingBeforeAfter}
                  className="w-full"
                >
                  {isGenerating
                    ? 'Generating Image...'
                    : 'Create Before & After'}
                </Button>
              </>
            ) : (
              <div className="text-center text-gray-400 py-10">
                Select 2 photos to preview
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loading Overlay */}
      {isLoadingEditor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 shadow-2xl flex flex-col items-center gap-4 max-w-sm mx-4">
            <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Switching to Edit Mode
              </h3>
              <p className="text-sm text-gray-500">
                Preparing your photo for editing...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Photo Editor Modal */}
      {showEditor && currentEditingImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">


          <div className="p-4">
            <ReactPhotoEditor
              open={showEditor}
              onClose={handleCloseEditor}
              file={currentEditingImage}
              onSaveImage={handleSaveEditedPhoto}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default BeforeAfter;
